import { useCallback, useMemo, type ReactNode } from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { PowerSyncProvider as SharedProvider } from '@helios/powersync-client-web';
import { getIdToken } from '@helios/auth-firebase-web';
import { makeFirebaseTokenProvider } from '@helios/powersync-auth-bridge-web';
import { AppSchema } from '@helios/powersync-schema';
import { FirebasePowerSyncConnector } from './connector';

const queryClient = new QueryClient();

/** Derive org slug from VITE_CLIENT_ID env var, fallback to 'default'. */
const orgSlug = import.meta.env.VITE_CLIENT_ID || 'default';

/** Multi-tab only works reliably on desktop browsers (matches PowerSync SDK defaults). */
const isMobileOrSafari =
  /Android|iPhone|iPod|iPad/i.test(navigator?.userAgent ?? '') || 'safari' in window;
const canEnableMultiTabs = typeof SharedWorker !== 'undefined' && !isMobileOrSafari;

/** Optional syncWrite endpoint for server persistence. */
const syncWriteUrl = import.meta.env.VITE_SYNCWRITE_URL || undefined;
console.log('[PowerSync] Config', { syncWriteUrl: syncWriteUrl ?? '(not set)' });

interface PowerSyncProviderProps {
  children: ReactNode;
  powerSyncUrl: string;
}

/**
 * App-specific PowerSync + TanStack Query provider.
 * Delegates to the shared provider with app-specific schema and connector.
 */
export function PowerSyncProvider({ children, powerSyncUrl }: PowerSyncProviderProps) {
  const createConnector = useCallback(() => {
    const tokenProvider = makeFirebaseTokenProvider(getIdToken, powerSyncUrl);
    return new FirebasePowerSyncConnector(() => tokenProvider(), syncWriteUrl);
  }, [powerSyncUrl]);

  const dbFilename = useMemo(() => `powercareai-${orgSlug}-web-admin.db`, []);

  return (
    <QueryClientProvider client={queryClient}>
      <SharedProvider
        powerSyncUrl={powerSyncUrl}
        schema={AppSchema}
        dbFilename={dbFilename}
        enableMultiTabs={canEnableMultiTabs}
        createConnector={createConnector}
      >
        {children}
      </SharedProvider>
    </QueryClientProvider>
  );
}
